import { useCallback, useEffect, useState } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { useActiveSlideNotes } from "../hooks/useActiveSlideNotes";
import { useSpeakerNotes } from "../hooks/useSpeakerNotes";
import { getErrorMessage, logSpeakerNotesError, reportSpeakerNotesFailure } from "../services/speakerNotesApi";
import styles from "./SpeakerNotesPanel.module.css";

/** How long a status banner (apply confirmation, copy feedback) stays visible before auto-dismissing. */
const BANNER_DISPLAY_MS = 2500;

type CopyFeedback = "copied" | "failed";

interface SpeakerNotesPanelProps {
  readonly onBack?: () => void;
}

function renderCurrentNotes(loading: boolean, notes: string) {
  if (loading) return <p className={styles.currentNotesEmpty}>Loading notes…</p>;
  if (notes) return <p className={styles.currentNotesText}>{notes}</p>;
  return <p className={styles.currentNotesEmpty}>No speaker notes for this slide yet.</p>;
}

export function SpeakerNotesPanel({ onBack }: Readonly<SpeakerNotesPanelProps>) {
  const { notes: currentNotes, loading: currentNotesLoading, refresh: refreshCurrentNotes } = useActiveSlideNotes();
  const { state, generate, apply, reset } = useSpeakerNotes();
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback | null>(null);

  const isBusy = state.status === "generating";
  const generatedNotes = state.status === "done" ? state.notes : "";
  const showDraft = state.status === "done";

  // After a successful apply, refresh Current Notes and collapse the draft
  // card so the applied text isn't shown twice.
  useEffect(() => {
    if (state.status !== "applied") return;
    let cancelled = false;
    refreshCurrentNotes()
      .catch((error: unknown) =>
        logSpeakerNotesError(
          "speaker-notes-current-notes-refresh",
          getErrorMessage(error, "Failed to load current speaker notes"),
        ),
      )
      .finally(() => {
        if (cancelled) return;
        setConfirmation("Added to slide notes.");
        reset();
      });
    return () => {
      cancelled = true;
    };
  }, [state.status, refreshCurrentNotes, reset]);

  useEffect(() => {
    if (!confirmation) return;
    const timer = setTimeout(() => setConfirmation(null), BANNER_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [confirmation]);

  useEffect(() => {
    if (!copyFeedback) return;
    const timer = setTimeout(() => setCopyFeedback(null), BANNER_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [copyFeedback]);

  const handleGenerateClick = useCallback(() => {
    generate().catch(reportSpeakerNotesFailure("speaker-notes-generate-ui"));
  }, [generate]);

  const handleApply = useCallback(
    (mode: "append" | "replace") => {
      setIsApplying(true);
      apply(mode)
        .catch(reportSpeakerNotesFailure("speaker-notes-apply-ui"))
        .finally(() => setIsApplying(false));
    },
    [apply],
  );

  const handleCopyClick = useCallback(() => {
    navigator.clipboard.writeText(generatedNotes).then(
      () => setCopyFeedback("copied"),
      (error: unknown) => {
        reportSpeakerNotesFailure("speaker-notes-copy-ui")(error);
        setCopyFeedback("failed");
      },
    );
  }, [generatedNotes]);

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Speaker Notes" subtitle="Deckorate" onBack={onBack} />
      <div className={styles.body}>
        <section className={styles.currentNotesSection} aria-label="Current speaker notes">
          <p className={styles.currentNotesLabel}>Current Notes</p>
          {renderCurrentNotes(currentNotesLoading, currentNotes)}
        </section>

        {confirmation && (
          <div className={`${styles.statusBox} ${styles.statusSuccess}`}>{confirmation}</div>
        )}
        {copyFeedback === "copied" && (
          <div className={`${styles.statusBox} ${styles.statusSuccess}`}>Copied.</div>
        )}
        {copyFeedback === "failed" && (
          <div className={`${styles.statusBox} ${styles.statusError}`}>
            Couldn't copy — select the text above instead.
          </div>
        )}

        <p className={styles.desc}>
          Draft AI speaker notes for the selected slide from its text and table content.
        </p>

        <button
          type="button"
          className={styles.generateBtn}
          onClick={handleGenerateClick}
          disabled={isBusy}
        >
          {isBusy ? "Drafting speaker notes…" : "Generate Speaker Notes"}
        </button>

        {state.status === "error" && (
          <div className={`${styles.statusBox} ${styles.statusError}`}>
            <span className={styles.statusLabel}>Failed:</span>
            {state.message}
          </div>
        )}

        {showDraft && (
          <div className={styles.resultCard}>
            <textarea
              className={styles.notesArea}
              value={generatedNotes}
              readOnly
              rows={8}
              aria-label="Drafted speaker notes"
            />
            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => handleApply("append")}
                disabled={isApplying}
              >
                {isApplying ? "Adding…" : "Add to Notes"}
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => handleApply("replace")}
                disabled={isApplying}
              >
                {isApplying ? "Replacing…" : "Replace Notes"}
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleCopyClick}
                disabled={isApplying}
              >
                Copy
              </button>
              <button type="button" className={styles.discardBtn} onClick={reset} disabled={isApplying}>
                Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
